import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const BUILD_GOALS = [
  "Just exploring",
  "Starting my first pop-up",
  "Already operating",
  "Want to grow my existing business",
  "Interested in operating Smash Lemonade",
];

const F = ({ label, children }) => (
  <div>
    <Label className="mono text-[11px] tracking-widest text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

/** Shared lead-capture form. `fields` picks which inputs show. */
export const LeadForm = ({
  source,
  productKey = "",
  fields = ["first_name", "email"],
  submitLabel = "GET THE FREE GUIDE",
  onDone,
  testId = "lead-form",
}) => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "", state: "", goal: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const has = (k) => fields.includes(k);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/leads", { ...form, source, product_key: productKey });
      if (onDone) onDone(form);
      else setDone(data.message);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div
        data-testid={`${testId}-success`}
        className="border border-primary/60 bg-[#191603] p-6 text-center"
      >
        <p className="display neon text-2xl md:text-3xl">{done}</p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid={testId} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {has("first_name") && (
          <F label="FIRST NAME">
            <Input data-testid={`${testId}-first-name`} required value={form.first_name}
              onChange={set("first_name")} className="rounded-sm bg-[#0A0A0A]" placeholder="Jordan" />
          </F>
        )}
        {has("last_name") && (
          <F label="LAST NAME">
            <Input data-testid={`${testId}-last-name`} value={form.last_name}
              onChange={set("last_name")} className="rounded-sm bg-[#0A0A0A]" placeholder="Ahquin" />
          </F>
        )}
        {has("email") && (
          <F label="EMAIL ADDRESS">
            <Input data-testid={`${testId}-email`} type="email" required value={form.email}
              onChange={set("email")} className="rounded-sm bg-[#0A0A0A]" placeholder="you@email.com" />
          </F>
        )}
        {has("phone") && (
          <F label="PHONE (OPTIONAL)">
            <Input data-testid={`${testId}-phone`} value={form.phone} onChange={set("phone")}
              className="rounded-sm bg-[#0A0A0A]" placeholder="801-555-0134" />
          </F>
        )}
        {has("city") && (
          <F label="CITY">
            <Input data-testid={`${testId}-city`} value={form.city} onChange={set("city")}
              className="rounded-sm bg-[#0A0A0A]" placeholder="Provo" />
          </F>
        )}
        {has("state") && (
          <F label="STATE">
            <Input data-testid={`${testId}-state`} value={form.state} onChange={set("state")}
              className="rounded-sm bg-[#0A0A0A]" placeholder="UT" />
          </F>
        )}
      </div>

      {has("goal") && (
        <F label="WHAT ARE YOU TRYING TO BUILD?">
          <select
            data-testid={`${testId}-goal`}
            value={form.goal}
            onChange={set("goal")}
            className="mono w-full rounded-sm border border-border bg-[#0A0A0A] px-3 py-2 text-sm text-white"
          >
            <option value="">Select one</option>
            {BUILD_GOALS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </F>
      )}

      {error && (
        <div data-testid={`${testId}-error`}
          className="border border-destructive/60 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <Button data-testid={`${testId}-submit`} type="submit" disabled={busy}
        className="display w-full rounded-sm bg-primary text-lg uppercase text-primary-foreground hover:bg-[#E6C800]">
        {busy ? "Sending..." : submitLabel}
      </Button>
    </form>
  );
};
